/**
 * DDF analytics tracker — fire-and-forget event POSTs to /api/analytics/event.
 * Persists a stable visitorId (localStorage) and per-tab sessionId (sessionStorage).
 * Uses sendBeacon for the unload session_end event so we don't block navigation.
 *
 * Security note (re. code-review localStorage warning):
 *   The four keys below store only non-sensitive client-generated tokens — a
 *   random visitor UUID, a random session UUID, a session timestamp and a
 *   first-visit boolean marker. NO PII, auth tokens or credentials are kept
 *   in storage by this module.
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ENDPOINT = `${BACKEND_URL}/api/analytics/event`;

const VISITOR_KEY = "ddf_visitor_id";
const SESSION_KEY = "ddf_session_id";
const SESSION_START_KEY = "ddf_session_started_at";
const FIRST_VISIT_KEY = "ddf_first_visit_done";

function uuid() {
  // Prefer crypto.randomUUID when available; else RFC4122 v4 fallback.
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getVisitorId() {
  let id = localStorage.getItem(VISITOR_KEY);
  if (!id) {
    id = uuid();
    localStorage.setItem(VISITOR_KEY, id);
  }
  return id;
}

function getSessionId() {
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = uuid();
    sessionStorage.setItem(SESSION_KEY, id);
    sessionStorage.setItem(SESSION_START_KEY, String(Date.now()));
  }
  return id;
}

function isFirstVisit() {
  // Marked once per browser — used by the backend to compute new vs returning.
  if (localStorage.getItem(FIRST_VISIT_KEY)) return false;
  localStorage.setItem(FIRST_VISIT_KEY, "1");
  return true;
}

function classifySource(referrer) {
  if (!referrer) return "direct";
  const r = referrer.toLowerCase();
  if (/facebook\.|fb\.|instagram\.|twitter\.|x\.com|tiktok\.|weibo\.|weixin\.|wechat/.test(r)) return "social";
  if (/google\.|bing\.|yahoo\.|duckduckgo\.|baidu\./.test(r)) return "search";
  return "referral";
}

export function trackEvent(eventType, meta = {}) {
  try {
    const payload = {
      eventType,
      sessionId: getSessionId(),
      visitorId: getVisitorId(),
      meta,
      referrer: document.referrer || "",
      source: classifySource(document.referrer || ""),
      isFirstVisit: false,
    };
    fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  } catch (err) {
    // Analytics must never break the user-facing app. Surface in dev for visibility.
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      if (process.env.NODE_ENV !== "production") console.warn("trackEvent failed", err);
    }
  }
}

export function trackPageView() {
  const first = isFirstVisit();
  // Touch the session id to make sure it exists with a start time
  getSessionId();
  const payload = {
    eventType: "page_view",
    sessionId: getSessionId(),
    visitorId: getVisitorId(),
    meta: { path: location.pathname },
    referrer: document.referrer || "",
    source: classifySource(document.referrer || ""),
    isFirstVisit: first,
  };
  fetch(ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {});
}

function sendSessionEnd() {
  const started = parseInt(sessionStorage.getItem(SESSION_START_KEY) || "0", 10);
  const durationSec = started ? Math.max(0, Math.round((Date.now() - started) / 1000)) : 0;
  const body = JSON.stringify({
    eventType: "session_end",
    sessionId: getSessionId(),
    visitorId: getVisitorId(),
    meta: { durationSec },
    referrer: document.referrer || "",
    source: classifySource(document.referrer || ""),
    isFirstVisit: false,
  });
  if (navigator.sendBeacon) {
    const blob = new Blob([body], { type: "application/json" });
    navigator.sendBeacon(ENDPOINT, blob);
  } else {
    fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  }
}

let sessionEndBound = false;
export function bindSessionEnd() {
  if (sessionEndBound) return;
  sessionEndBound = true;
  // Send on tab close / hide. Use both events for cross-browser safety.
  window.addEventListener("pagehide", sendSessionEnd);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") sendSessionEnd();
  });
}

export function trackDrawSectionView() {
  trackEvent("draw_section_view", {});
}

export function resetDrawSentForTests() {
  // No-op: the guard now lives in the React component as a ref.
}
